import { useMicrocopy } from '@contexts/MicrocopyContext';
import { MicrocopyFragment } from '@graphql/generated-contentful/graphql';
import { SaleType } from '@lib/types/Sale';
import c from 'classnames';
import Image from 'next/image';
import { Star } from 'phosphor-icons';
import { Label, Link } from 'ui-components';
import { DEFAULT_LOCALE } from 'utils';
import { ProductCardPriceSection } from './ProductCardPriceSection';

export type Sale = {
  type: SaleType;
  dates: {
    starts: Date;
    ends: Date;
  };
};

export type ProductCardProps = {
  name: string;
  productId: string;
  imageUrl: string;
  publisher?: {
    name: string;
    id: string;
  };
  rating?: number;
  price: {
    currentPrice: number;
    originalPrice: number;
    currency?: string;
    discountPercent?: number;
  };
  sale?: Sale;
  label?: {
    text: string;
    iconName?: string;
  };
  isPurchased?: boolean;
  locale?: string;
  onClick?: () => void;
  extraEventHandlers?: {
    onClick?: () => void;
    onMouseDown?: () => void;
    onMouseUp?: () => void;
    onContextMenu?: () => void;
    onTouchStart?: () => void;
    onTouchEnd?: () => void;
  };
  className?: string;
};

export const ProductCard = ({
  name,
  productId,
  imageUrl,
  publisher,
  rating = 0,
  price,
  sale,
  label,
  isPurchased = false,
  locale = DEFAULT_LOCALE,
  extraEventHandlers = {},
  className = '',
}: ProductCardProps) => {
  const microcopy = useMicrocopy<{
    productCardMicrocopy: MicrocopyFragment[];
  }>();
  const purchasedMicrocopy =
    microcopy?.productCardMicrocopy?.find(
      (microcopyFragment) => microcopyFragment.key === 'product-card.purchased'
    )?.value ?? 'Purchased';

  return (
    <article
      data-test={`search-results-product-card-${productId}`}
      className={c(
        'relative block rounded-2xl border border-grey-200 p-0 lg:border-none lg:p-5 lg:pb-6 lg:hover:shadow-card',
        className
      )}
    >
      <div className="relative aspect-3/2">
        {isPurchased && (
          <Label
            testId="product-card-purchased-label"
            appearance="item"
            text={purchasedMicrocopy}
            className="absolute left-0 top-3 z-10 !px-1 lg:top-[0.62rem] lg:!px-4"
          />
        )}
        <Image
          fill
          sizes="50vw, (min-width: 768px) 25vw, (min-width: 1024px) 33vw"
          alt=""
          aria-hidden={true}
          className="rounded-t-lg object-cover"
          src={imageUrl}
        />
      </div>
      <div className="p-2 pb-3 lg:px-2 lg:pb-0 lg:pt-3">
        <div className="mb-2 flex items-center justify-between">
          {publisher && (
            <Link
              data-test="product-card-publisher"
              className="caption focus-outline relative z-10 overflow-hidden text-ellipsis whitespace-nowrap rounded text-xxs/5 text-grey-600 lg:text-sm"
              href={`/publishers/${publisher.id}`}
            >
              {publisher.name}
            </Link>
          )}
          {rating ? (
            <div
              data-test="product-card-rating"
              className="flex items-center text-xxs/5 lg:text-base/6"
            >
              <Star
                weight="fill"
                aria-hidden={true}
                className="mr-1 h-3 w-3 lg:h-4 lg:w-4"
              />
              <div>{rating}</div>
            </div>
          ) : null}
        </div>
        <div className="flex flex-col">
          <Link
            href={`/packages/package/${productId}`}
            data-test="product-card-name"
            // after pseudo-element makes the whole card clickable
            // wrapping the entire card in a link would result in invalid HTML with a nested link for publisher inside
            className="focus-outline line-clamp-2 rounded text-sm font-medium tracking-tight text-grey-700 after:absolute after:bottom-0 after:left-0 after:right-0 after:top-0 lg:text-base"
            {...extraEventHandlers}
          >
            {name}
          </Link>
          <ProductCardPriceSection price={price} sale={sale} locale={locale} />
          {label && (
            <Label
              appearance="editorial"
              iconName={label.iconName}
              text={label.text}
              className="mt-2"
            />
          )}
        </div>
      </div>
    </article>
  );
};
